import { useState, useRef } from 'react';

/**
 * Hook para buscar endereço pelo CEP usando BrasilAPI
 * @returns {object} { data, loading, error, fetchCep }
 *
 * Exemplo de uso:
 * const { data, loading, error, fetchCep } = useCepLookup();
 * fetchCep('01001-000');
 */
export function useCepLookup() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const currentRequestRef = useRef(null);

  async function fetchCep(cep) {
    // Cancelar requisição anterior se existir
    if (currentRequestRef.current) {
      currentRequestRef.current.abort();
    }

    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) {
      setError('CEP inválido');
      return;
    }

    // Criar novo AbortController
    const controller = new AbortController();
    currentRequestRef.current = controller;

    setLoading(true);
    setError(null);
    setData(null);

    try {
      const res = await fetch(`https://brasilapi.com.br/api/cep/v2/${cleanCep}`, {
        signal: controller.signal
      });
      
      if (!res.ok) throw new Error('CEP não encontrado');
      
      const json = await res.json();
      
      // Verificar se a requisição não foi cancelada
      if (!controller.signal.aborted) {
        setData(json);
      }
    } catch (err) {
      // Ignorar erros de cancelamento
      if (!controller.signal.aborted) {
        setError(err.message || 'Erro ao buscar CEP');
      }
    } finally {
      // Só atualizar loading se a requisição não foi cancelada
      if (!controller.signal.aborted) {
        setLoading(false);
      }
      currentRequestRef.current = null;
    }
  }

  return { data, loading, error, fetchCep };
}
